import { memo, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { clearMessages } from 'store/ancillary/actions';
import { raiseErrorBanner } from 'components/MessageDispatch/messageDispatch';
import DOMPurify from 'dompurify';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import CircularProgress from '@mui/material/CircularProgress';

import { getJobCategory, search } from '../_api';
import { debounceAsync } from 'utils/misc/debounce';
import { JobCategory, OBJ_APP_BRIDGES } from '../_constants';

export const JobCategoryAutocomplete = memo(function JobCategoryAutocomplete({
	onChange,
	onIconClick,
	jcID,
	jcAppID,
	jcName,
	jcType,
	readOnly = false,
	systemID = OBJ_APP_BRIDGES.ID,
	validationMessage = '',
}) {
	const ROW_CURRENT_OPTION = new JobCategory({
		ID: jcID,
		AppID: jcAppID,
		Name: jcName,
		Type: jcType,
	});

	const dispatch = useDispatch();
	const [sFocused, setFocused] = useState(false);
	const [sLoading, setLoading] = useState(false);
	const [sOptions, setOptions] = useState([ROW_CURRENT_OPTION]);
	const [sInputValue, setInputValue] = useState(ROW_CURRENT_OPTION.name);
	/**
	 * Represents the `readOnly` prop on initial render or its previous value if the prop changes (before being re-synced).
	 * @type [boolean, Function]
	 */
	const [sWasDisabled, setWasDisabled] = useState(readOnly);

	/**
	 * Force a re-render to sync locally controlled input value with parent-controlled component value when component's disabled state changes.
	 * The component's value could diverge from local, controlled state when it is disabled by its parent after once being enabled.
	 */
	if (readOnly !== sWasDisabled) {
		setWasDisabled(readOnly);
		setOptions([ROW_CURRENT_OPTION]);
		setInputValue(jcName);
	}

	const debouncedHelp = useMemo(() => {
		return debounceAsync(async idJC => {
			try {
				const jobCategory = await getJobCategory(idJC);
				onIconClick(jobCategory);
			} catch (ex) {
				dispatch(clearMessages());
				raiseErrorBanner(ex, dispatch);
			}
		}, 250);
	}, [onIconClick, dispatch]);
	/**
	 * Memoized function required in order to preserve timeout IDs, otherwise function will be created new each render.
	 */
	const debouncedSearch = useMemo(() => {
		return debounceAsync(async input => {
			try {
				if (input) {
					let arrResults = await search(
						DOMPurify.sanitize(input),
						systemID,
					);
					setOptions(arrResults);
				} else {
					setOptions([]);
				}
			} catch (ex) {
				dispatch(clearMessages());
				raiseErrorBanner(ex, dispatch);
			} finally {
				setLoading(false);
			}
		});
	}, [dispatch, systemID]);

	return (
		<FormControl margin='none' onClick={e => e.stopPropagation()} fullWidth>
			<Autocomplete
				disableClearable
				disabled={readOnly}
				forcePopupIcon={true}
				filterOptions={x => x}
				getOptionLabel={option => option?.name ?? ''}
				loading={sLoading}
				loadingText='Searching...'
				noOptionsText='No results'
				renderInput={params => (
					<TextField
						{...params}
						error={!!validationMessage}
						helperText={validationMessage}
						hiddenLabel
						InputProps={{
							...params?.InputProps,
							endAdornment: (
								<>
									{(sLoading || sFocused) && sInputValue ? (
										<InputAdornment
											sx={{
												marginBottom: '8px',
											}}
											position='end'
										>
											{sLoading ? (
												<CircularProgress
													size={20}
													variant='indeterminate'
												/>
											) : (
												<IconButton
													sx={{ padding: '0' }}
													aria-label='Help'
													title='Help'
													edge='end'
													size='small'
													onClick={() => {
														if (jcID !== -1) {
															debouncedHelp(jcID);
														}
													}}
												>
													<InfoIcon fontSize='small' />
												</IconButton>
											)}
										</InputAdornment>
									) : null}
									{params?.InputProps?.endAdornment}
								</>
							),
							placeholder:
								sFocused && jcID === -1
									? 'Search for a job category...'
									: '',
						}}
						onFocus={() => {
							setFocused(true);
						}}
						onBlur={() => {
							setFocused(false);
						}}
						variant='standard'
						multiline
					/>
				)}
				size='small'
				options={sOptions}
				onBlur={() => {
					setOptions([ROW_CURRENT_OPTION]);
					setInputValue(ROW_CURRENT_OPTION.name);
				}}
				onInputChange={(e, newInput, reason) => {
					// exclude change event when initially set
					if (e) {
						setInputValue(newInput);
						if (reason === 'input') {
							setLoading(true);
							debouncedSearch(newInput);
						}
					}
				}}
				inputValue={readOnly ? jcName : sInputValue}
				isOptionEqualToValue={(option, value) => option.name === value}
				onChange={(_, newValue) => {
					onChange(newValue);
				}}
				value={sInputValue !== jcName ? null : jcName}
			/>
		</FormControl>
	);
});
